import type {MergedRecommendation, Recommendation} from '@package/shared'
import {useCallback} from 'react'
import {EditModeButtons} from '../../components/EditModeButtons.tsx'
import type {DateRange} from '../../../../components/dateRangeSelector/DateRangeSelector.tsx'
import type {AcceptRecommendationRequest} from '@app/api-resolver/router'
import {TimeEntryFormFields} from '../TimeEntryFormFields.tsx'
import {TimeEntryFormValues} from '../TimeEntryFormValues.ts'
import {useForm} from '@mantine/form'
import type {PartialTimeEntryFormValues} from '../PartialTimeEntryFormValues.ts'
import {zod4Resolver} from 'mantine-form-zod-resolver'

type Props<TRecommendation extends Recommendation | MergedRecommendation> = {
  day: DateRange
  recommendation: TRecommendation
  onAccept: (request: AcceptRecommendationRequest) => Promise<void>
  accepting: boolean
  onReject: () => Promise<void>
  rejecting: boolean
  disabled: boolean
}

export const AcceptRecommendationForm = <TRecommendation extends Recommendation | MergedRecommendation>({
  day,
  recommendation,
  onAccept,
  accepting,
  onReject,
  rejecting,
  disabled: containerDisabled,
}: Props<TRecommendation>) => {
  const form = useForm<PartialTimeEntryFormValues, TimeEntryFormValues | undefined>({
    initialValues: recommendation satisfies PartialTimeEntryFormValues,
    validate: zod4Resolver(TimeEntryFormValues),
    transformValues: (values) => TimeEntryFormValues.safeParse(values).data,
  })

  const handleSubmit = useCallback(
    async (values: TimeEntryFormValues | undefined) => {
      if (!values) return
      await onAccept({
        ...recommendation,
        ...values,
      } satisfies AcceptRecommendationRequest)
    },
    [onAccept, recommendation],
  )

  const disabled = containerDisabled

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} style={{display: 'flex', flexDirection: 'column', gap: 5}}>
      <TimeEntryFormFields form={form} day={day} disabled={disabled} />

      <EditModeButtons
        submitVariant={'accept'}
        submitting={accepting}
        destructiveVariant={'reject'}
        onDestruct={onReject}
        destructing={rejecting}
        disabled={disabled}
      />
    </form>
  )
}
